<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { computed, type ComputedRef } from 'vue';

import ExpandableCard from '~/components/shared/ExpandableCard.vue';
import { ContractFormalizationAPI } from '~/services/contract-formalization-api/contract-formalization-api.types';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';

import SimulationStep from './steps/3.SimulationStep.vue';
import EquipmentStep from './steps/4.EquipmentStep.vue';
import CustomerDataStep from './steps/5.CustomerDataStep.vue';
import BiometryAndContractStep from './steps/6.BiometryAndContractStep.vue';
import PaymentModelStep from './steps/7.PaymentModelStep.vue';
import InstallationStep from './steps/8.InstallationStep.vue';

interface SectionStatus {
  completedAt: ComputedRef<string>;
  lastSubmittedAt: ComputedRef<string>;
  text: ComputedRef<string>;
  type: ComputedRef<string>;
}

interface Props {
  biometrySection?: SectionStatus;
  contractSection?: SectionStatus;
  financing?: FinancingFlowApi.BaseResponse<FinancingFlowApi.FinancingV2>;
  formalizationInfo?: ContractFormalizationAPI.FormalizationsInfoResponse;
}

interface Emits {
  (_e: 'refresh'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

function processStatus(status: string) {
  const statusMap: Record<string, { text: string; type: string }> = {
    aguardando: { text: 'Aguardando', type: 'warning' },
    approved: { text: 'Aprovado', type: 'positive' },
    aprovado: { text: 'Aprovado', type: 'positive' },
    completed: { text: 'Concluído', type: 'positive' },
    concluído: { text: 'Concluído', type: 'positive' },
    'em andamento': { text: 'Em andamento', type: 'informative' },
    in_progress: { text: 'Em andamento', type: 'informative' },
    revisar: { text: 'Revisar', type: 'warning' },
    to_review: { text: 'Revisar', type: 'warning' },
    under_analysis: { text: 'Em Análise', type: 'informative' },
    waiting: { text: 'Aguardando', type: 'warning' },
  };

  const statusLower = status?.toLowerCase().trim() || '';
  return statusMap[statusLower] || { text: status || 'Aguardando', type: 'neutral' };
}

const STEPS_SEQUENCE: FinancingFlowApi.FinancingSteps[] = [
  'simulation',
  'equipment',
  'customer_data',
  'biometry_and_contract',
  'payment_model',
  'installation',
];

const STEPS_LABELS = {
  biometry_and_contract: 'Biometria e Contrato',
  customer_data: 'Cliente',
  equipment: 'Equipamento',
  installation: 'Instalação',
  payment_model: 'Modelo de Recebimento',
  simulation: 'Simulação',
};

const STEP_COMPONENTS = {
  biometry_and_contract: BiometryAndContractStep,
  customer_data: CustomerDataStep,
  equipment: EquipmentStep,
  installation: InstallationStep,
  payment_model: PaymentModelStep,
  simulation: SimulationStep,
};

function isStepCompleted(step: FinancingFlowApi.FinancingSteps): boolean {
  if (!props.financing?.data?.section_statuses) return false;
  const statuses = props.financing.data.section_statuses;
  return !!statuses[step]?.completed_at;
}

function getStepStatus(step: FinancingFlowApi.FinancingSteps) {
  if (!props.financing?.data) return undefined;

  const currentStep = props.financing.data.step as FinancingFlowApi.FinancingSteps;
  const currentStepIndex = STEPS_SEQUENCE.indexOf(currentStep);
  const stepIndex = STEPS_SEQUENCE.indexOf(step);

  if (stepIndex > currentStepIndex && !isStepCompleted(step)) {
    return undefined;
  }

  if (isStepCompleted(step)) {
    return processStatus('concluído');
  }

  if (step === currentStep) {
    const apiStatus = props.financing.data.section_statuses?.[step]?.status;
    return processStatus(apiStatus || 'em andamento');
  }

  return processStatus('aguardando');
}

const stepsConfig = computed(() => {
  if (!props.financing?.data) return [];

  const currentStep = props.financing.data.step as FinancingFlowApi.FinancingSteps;
  const currentStepIndex = STEPS_SEQUENCE.indexOf(currentStep);

  return STEPS_SEQUENCE.map((step, index) => {
    const isCompleted = isStepCompleted(step);
    const isCurrent = step === currentStep;
    const isUnlocked = index <= currentStepIndex || isCompleted;
    const shouldExpand = isCurrent;

    return {
      component: STEP_COMPONENTS[step],
      defaultExpanded: shouldExpand,
      isCurrentStep: isCurrent,
      isLocked: !isUnlocked,
      key: step,
      status: getStepStatus(step),
      title: STEPS_LABELS[step],
    };
  });
});
</script>

<template>
  <div class="financing-steps-flow">
    <div class="row g-3">
      <div v-for="(step, index) in stepsConfig" :key="step.key" class="col-12">
        <ExpandableCard
          :title="`${index + 1}. ${step.title}`"
          :is-locked="step.isLocked"
          :status="step.status"
          :default-expanded="step.defaultExpanded"
        >
          <component
            :is="step.component"
            v-if="step.component && !step.isLocked && financing?.data"
            :financing="financing.data"
            :biometry-section="step.key === 'biometry_and_contract' ? biometrySection : undefined"
            :contract-section="step.key === 'biometry_and_contract' ? contractSection : undefined"
            @refresh="emit('refresh')"
          />
          <div v-else-if="step.isLocked" class="text-muted">
            <p>Informações desta etapa serão exibidas quando ela estiver ativa.</p>
          </div>
        </ExpandableCard>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.financing-steps-flow {
  .card-title {
    font-weight: 600;
  }

  :deep(.card) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e3e6f0;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }
  }
}
</style>
